import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, Lock, User } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields";
import { useLogin } from "../../hooks/useAuth";

const loginSchema = z.object({
  username: z.string().trim().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const loginMutation = useLogin();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await loginMutation.mutateAsync({
        username: values.username,
        password: values.password,
      });
    } catch {
      // Handled by mutation / error state
    }
  };

  const isLoading = isSubmitting || loginMutation.isPending;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="w-full glass rounded-[30px] p-8 sm:p-10 shadow-2xl relative overflow-hidden"
    >
      {/* Decorative inner glow */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

      {/* Logo for mobile/tablet */}
      <div className="flex justify-center mb-8">
        <img
          src="https://www.drcsystems.com/wp-content/themes/drc2026/assets/images/logo.svg"
          alt="DRC Systems"
          className="h-10 object-contain"
        />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <TextInput
          control={control}
          name="username"
          label="Username"
          placeholder="Enter your username"
          prefix={<User className="h-4 w-4 text-slate-400" />}
          autoCapitalize="none"
          autoComplete="username"
          required
        />

        <TextInput
          control={control}
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          placeholder="Enter your password"
          prefix={<Lock className="h-4 w-4 text-slate-400" />}
          suffix={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          }
          autoComplete="current-password"
          required
        />

        {loginMutation.isError && (
          <div className="p-3 text-xs font-medium text-red-600 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-200 dark:border-red-800">
            {loginMutation.error?.message ||
              "Invalid credentials. Please try again."}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-11 rounded-xl bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/25 transition-all active:scale-[0.98]"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            <Lock className="mr-2 h-4 w-4" />
          )}
          Sign In
        </Button>
      </form>
    </motion.div>
  );
}

export default LoginForm;
